import React from 'react';
import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import { eyebrowSx, heroPanelSx, pageHeadlineSx, sectionCardSx, subtleCopySx, warmPanelSx } from './designSystem';

const sections = [
  {
    title: 'Information We Collect',
    body: 'We may collect account details such as name, email address, username, organization name, role, and workspace settings. When the browser extension or dashboard is used, we may also process policy rules, allowlists, blocklists, profile assignments, scheduling settings, device enrollment details, diagnostics, and filtering events needed to operate the service.',
  },
  {
    title: 'How Information Is Used',
    body: 'We use information to authenticate users, create and manage school workspaces, apply configured filtering rules, support reporting and troubleshooting, improve reliability, and communicate with customers about the service. We do not describe this service as an advertising platform, and this policy is intended to reflect product operation rather than ad-targeting use.',
  },
  {
    title: 'School and Student Context',
    body: 'When BlockWebAI is used by a school, district, or other educational organization, that organization may configure rules, devices, users, and reporting within its workspace. Schools are responsible for deciding how they deploy the service, what policies they configure, and what notices or permissions they provide to students, parents, and staff as required by their own obligations.',
  },
  {
    title: 'Sharing',
    body: 'We may share information with infrastructure, hosting, email, analytics, security, and other service providers that help us operate the product. We may also disclose information when required by law, to protect the service or users, or as part of a business transition. We do not sell personal information in the ordinary meaning of that phrase.',
  },
  {
    title: 'Retention',
    body: 'We retain information for as long as reasonably needed to operate the service, maintain security, support customers, comply with legal obligations, and resolve disputes. Retention may vary by data type, deployment, and customer relationship.',
  },
  {
    title: 'Security',
    body: 'We use reasonable administrative, technical, and organizational measures to protect information, but no system can be guaranteed to be perfectly secure. Customers should also use strong passwords, managed deployment practices, and appropriate device administration controls.',
  },
  {
    title: 'Your Choices',
    body: 'You may contact us to request updates to account information, discuss workspace administration, or ask questions about data handling. Organizations administering a school workspace may also control many settings directly inside the product.',
  },
  {
    title: 'Contact',
    body: 'Questions about this policy can be sent to jonathanlarsonbusiness@gmail.com.',
  },
];

function PrivacyPolicy() {
  return (
    <Container sx={{ py: { xs: 6, md: 8 }, maxWidth: '980px !important' }}>
      <Stack spacing={4}>
        <Box sx={{ ...heroPanelSx, p: { xs: 3.5, md: 5 } }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="overline" sx={eyebrowSx}>
                Privacy Policy
              </Typography>
              <Typography variant="h2" sx={{ ...pageHeadlineSx, mt: 1 }}>
                How BlockWebAI handles information
              </Typography>
              <Typography sx={{ ...subtleCopySx, mt: 2, lineHeight: 1.85 }}>
                This policy explains, at a high level, what information BlockWebAI for Schools may process and how that
                information is used in the product.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ ...warmPanelSx, p: 3 }}>
                <Typography variant="subtitle2" sx={{ color: '#8C6B1F', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                  Effective Date
                </Typography>
                <Typography sx={{ mt: 1.2, color: '#52677A', lineHeight: 1.75, fontWeight: 600 }}>
                  April 18, 2026
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Stack spacing={2.25}>
          {sections.map((section, index) => (
            <Box key={section.title} sx={{ ...sectionCardSx, p: { xs: 3, md: 3.5 } }}>
              <Stack direction="row" spacing={1.75} alignItems="baseline">
                <Typography
                  sx={{
                    color: '#13947F',
                    fontWeight: 800,
                    fontSize: '0.78rem',
                    fontVariantNumeric: 'tabular-nums',
                    flexShrink: 0,
                  }}
                >
                  {String(index + 1).padStart(2, '0')}
                </Typography>
                <Box>
                  <Typography variant="h5" sx={{ color: '#0B2E43', mb: 1.2 }}>
                    {section.title}
                  </Typography>
                  <Typography sx={{ ...subtleCopySx, lineHeight: 1.8 }}>{section.body}</Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Stack>
    </Container>
  );
}

export default PrivacyPolicy;
